<template>
  <aside class="ctx" aria-label="Context">
    <!-- Covers the panel with the AVA logo loader while the verification VM
         is being fetched. Separate from the viewer's overlay — the two
         surfaces load on independent signals (VM vs Forge download). -->
    <AvaLoader v-if="contextStatus.loading" overlay />
    <PanelHeader title="CONTEXT" />
    <div class="ctx-scroll">
      <div
        v-if="contextStatus.error || contextStatus.unavailable"
        class="ctx-state"
        :class="{ 'ctx-state--error': contextStatus.error }"
        :role="contextStatus.error ? 'alert' : 'status'"
      >
        <v-icon size="18">
          {{ contextStatus.error ? 'mdi-alert-circle-outline' : 'mdi-link-off' }}
        </v-icon>
        <span>{{ contextStatus.error || contextStatus.unavailable }}</span>
      </div>
      <SupportHeader
        v-if="supportHeader"
        :support="supportHeader"
        :chips="chips"
      />
      <ItemMetadata
        v-if="metadataItems.length"
        :items="metadataItems"
      />
      <TolerancePicker
        v-if="activePoint"
        class="ctx__tolerance"
        :tolerance="activePoint.tolerance"
        @change="onToleranceChange"
      />
      <!-- The point list is selection and status only. Workflow guidance lives
           in the single wizard over the measurement workspace. -->
      <PointList
        :points="points"
        :active-id="activePointId"
        :hidden-marker-point-ids="markerSettings.hiddenPointIds"
        @select="onSelectPoint"
        @add="onAddPoint"
        @remove="onRemovePoint"
        @rename="onRenamePoint"
        @toggle-markers="onTogglePointMarkers"
      />
      <!-- Expand has TWO outcomes and the panel needs both. `expand` is the
           FALLBACK (pop-up blocked, or an Office file the pop-out cannot
           render) and opens the in-app viewer; `popped-out` is the SUCCESS
           path and must CLOSE it, because "expand replaces the panel". Drop
           the second relay and a panel left over from an earlier fallback
           stays up showing a different drawing than the pop-out window. -->
      <DrawingThumbnail
        v-if="drawingDisplay"
        :drawing="drawingDisplay"
        :documents="referenceDocumentItems"
        :selected-document-id="selectedReferenceDocumentId"
        @select="onSelectReferenceDocument"
        @expand="$emit('open-drawing', $event)"
        @popped-out="$emit('close-drawing')"
      />
    </div>
  </aside>
</template>

<script>
import { mapGetters } from 'vuex';
import { log as debugLog } from '@/utils/debugLogger';
import { EventBus } from '@/event-bus';
import { CAPTURE_EVENTS } from '@/features/Verification/utils/captureEvents';
import { AvaLoader } from '@/components/loaders';
import PanelHeader from '@/features/Verification/components/shared/PanelHeader.vue';
import SupportHeader from '@/features/Verification/components/context/SupportHeader.vue';
import ItemMetadata from '@/features/Verification/components/context/ItemMetadata.vue';
import DrawingThumbnail from '@/features/Verification/components/context/DrawingThumbnail.vue';
import PointList from '@/features/Verification/components/context/PointList.vue';
import TolerancePicker from '@/features/Verification/components/results/TolerancePicker.vue';

export default {
  name: 'ContextPanel',
  components: {
    AvaLoader,
    PanelHeader,
    SupportHeader,
    ItemMetadata,
    DrawingThumbnail,
    PointList,
    TolerancePicker,
  },
  computed: {
    ...mapGetters('VERIFICATION', {
      supportHeader: 'GET_SUPPORT_HEADER',
      chips: 'GET_CHIPS',
      metadataItems: 'GET_METADATA_ITEMS',
      drawingDisplay: 'GET_DRAWING_DISPLAY',
      referenceDocumentItems: 'GET_REFERENCE_DOCUMENT_ITEMS',
      selectedReferenceDocumentId: 'GET_SELECTED_REFERENCE_DOCUMENT_ID',
      contextStatus: 'GET_CONTEXT_STATUS',
      points: 'GET_POINTS_WITH_RESULTS',
      activePoint: 'GET_ACTIVE_POINT',
      markerSettings: 'GET_MARKER_SETTINGS',
    }),
    activePointId() {
      return this.activePoint ? this.activePoint.id : null;
    },
  },
  methods: {
    async onSelectPoint(id) {
      debugLog('[ContextPanel] select point', id);
      await this.$store.dispatch('VERIFICATION/selectPoint', id);
      EventBus.$emit(CAPTURE_EVENTS.OPEN_WIZARD);
    },
    /**
     * Adding a point starts the one continuous workspace wizard. The list never
     * owns a second instruction card or a separate design dialog.
     */
    async onAddPoint() {
      debugLog('[ContextPanel] add point');
      const point = await this.$store.dispatch('VERIFICATION/addPoint');
      if (point) EventBus.$emit(CAPTURE_EVENTS.OPEN_WIZARD);
    },
    onRemovePoint(id) {
      debugLog('[ContextPanel] remove point', id);
      this.$store.dispatch('VERIFICATION/removePoint', id);
    },
    onRenamePoint(payload) {
      debugLog('[ContextPanel] rename point', payload);
      this.$store.dispatch('VERIFICATION/renamePoint', payload);
    },
    onTogglePointMarkers(pointId) {
      this.$store.commit('VERIFICATION/TOGGLE_POINT_MARKER', pointId);
    },
    onSelectReferenceDocument(documentId) {
      debugLog('[ContextPanel] select reference document', documentId);
      this.$store.dispatch('VERIFICATION/selectReferenceDocument', documentId);
    },
    onToleranceChange(tolerance) {
      if (!this.activePointId) return;
      debugLog('[ContextPanel] set tolerance', this.activePointId, tolerance);
      this.$store.dispatch('VERIFICATION/setTolerance', {
        id: this.activePointId,
        tolerance,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.ctx {
  background: $vf-panel;
  border-right: 1px solid $vf-line;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  // Anchor for <AvaLoader overlay> (position:absolute; inset:0) so the
  // backdrop clips to this panel rather than the workspace grid.
  position: relative;
}

// Shrink the logo 20% inside panel loaders without touching the backdrop —
// scaling the container keeps the backdrop full-sized so the panel stays
// fully covered while loading.
::v-deep .ava-loader__container {
  transform: scale(0.8);
}

.ctx-scroll {
  // flex-column so the last child (DrawingThumbnail) can dock to the bottom
  // using `margin-top: auto` when there's slack space. When content overflows
  // naturally, margin-auto collapses to 0 and the section just scrolls with
  // the rest — exactly the behaviour requested.
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  padding: 0 16px 16px;

  > :last-child {
    margin-top: auto;
  }
}

.ctx__tolerance {
  margin-top: 14px;
}

.ctx-state {
  margin: 12px 0 0;
  padding: 9px 10px;
  border: 1px solid $vf-line-soft;
  border-radius: $vf-r-sm;
  background: #f5f7f6;
  color: $vf-ink-2;
  font-size: 11px;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 7px;
}

.ctx-state--error {
  border-color: rgba(183, 28, 28, 0.25);
  background: rgba(183, 28, 28, 0.05);
  color: $vf-error;
}

// Remove the trailing dashed separator on the last block when populated
::v-deep .meta-block:last-child {
  border-bottom: 0;
}
</style>
